import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatMenuModule } from '@angular/material/menu';
import { BehaviorSubject, take } from 'rxjs';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import {
  ColorScheme,
  PencilBottomSheetContainerComponent,
} from 'src/app/standalones/components/pencil-bottom-sheet-container/pencil-bottom-sheet-container.component';
import {
  PencilButtonComponent,
  PencilButtonThemeEnum,
} from 'src/app/standalones/components/pencil-button/pencil-button.component';
import {
  IconTypes,
  PencilIconComponent,
} from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { PnclMenuHeaderComponent } from 'src/app/standalones/components/pncl-menu-header/pncl-menu-header.component';
import { FOLDER_NAME_MAX_LENGTH } from 'src/app/sessions/session/services/rename-titles.service';
import { ExtendedTextboxComponent } from 'src/app/ui/extended-textbox/extended-textbox.component';
import { UiModule } from 'src/app/ui/ui.module';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';

/**
 * A labelled, collapsible group of sidebar rows — the shape shared by Tools, Pinned and Spaces.
 *
 * The whole header is the toggle, so the chevron is an affordance rather than the control, and is
 * only revealed on hover.
 */
@Component({
  selector: 'app-nav-sidebar-section',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatMenuModule,
    PencilBottomSheetContainerComponent,
    PencilButtonComponent,
    PencilIconComponent,
    PnclMenuHeaderComponent,
    UiModule,
  ],
  templateUrl: './nav-sidebar-section.component.html',
  styleUrls: ['./nav-sidebar-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavSidebarSectionComponent implements OnDestroy {
  @Input() label = '';

  /**
   * Turns the label into an inline-editable textbox, driven by {@link startRename}. Folders use it;
   * the fixed sections (Pinned, Spaces) don't.
   */
  @Input() renameable = false;
  /** Distinguishes this section's textbox in the DOM; only read when `renameable`. */
  @Input() sectionId = '';
  @Output() renameCommitted = new EventEmitter<string>();

  @Input() expanded = true;
  /** Two-way bindable, so callers can persist or react to the state (e.g. re-measure the sidebar). */
  @Output() expandedChange = new EventEmitter<boolean>();

  /**
   * False for a section with nothing to show — an empty folder. It keeps its header (and its menu),
   * but loses the chevron and the toggle, since there is nothing to open.
   */
  @Input() expandable = true;

  /**
   * Turns the header into a drop list of its own, which is what lets a *collapsed* section be
   * dropped onto: its body is zero-height and inert, so the header is the only thing left to aim
   * at. Inert while expanded, where the body is the drop target. Empty disables it entirely.
   */
  @Input() headerDropListId = '';
  @Input() headerDropListConnectedTo: string[] = [];
  /** Fires while a drag is over the header — the parent's cue to start spring-opening. */
  @Output() headerDropListEntered = new EventEmitter<void>();
  @Output() headerDropListExited = new EventEmitter<void>();
  @Output() headerDropListDropped = new EventEmitter<CdkDragDrop<unknown>>();

  /**
   * The section's actions, supplied by the parent. A TemplateRef rather than projected content
   * because it's rendered in two places — the menu panel and, on small screens, a bottom sheet.
   * Providing it is what puts the "more" button in the header.
   */
  @Input() menuContent?: TemplateRef<unknown>;
  /** Passed through to `menuContent` as `$implicit`, so a shared template can act on this section. */
  @Input() menuContext?: unknown;
  @Input() menuTitle = '';

  @ViewChild('bottomSheetContainer') private bottomSheetContainer?: TemplateRef<unknown>;
  @ViewChild('labelTextbox') private labelTextbox?: ExtendedTextboxComponent;

  /** The label is only editable while this is true — see the note in the template. */
  readonly isRenaming$ = new BehaviorSubject(false);

  protected readonly FOLDER_NAME_MAX_LENGTH = FOLDER_NAME_MAX_LENGTH;

  /** Keeps the button on screen while its menu is open, even once the pointer leaves the header. */
  readonly isMenuOpen$ = new BehaviorSubject(false);

  protected readonly IconTypes = IconTypes;
  protected readonly PencilButtonThemeEnum = PencilButtonThemeEnum;
  protected readonly ColorScheme = ColorScheme;

  private bottomSheetRef?: MatBottomSheetRef;

  constructor(
    private bottomSheet: MatBottomSheet,
    private cdr: ChangeDetectorRef,
    protected screenSizeService: ScreenSizeService,
  ) {}

  /** The sheet lives in an overlay, so it outlives this component unless dismissed. */
  ngOnDestroy(): void {
    this.dismissBottomSheet();
  }

  toggle(): void {
    this.setExpanded(!this.expanded);
  }

  /**
   * Drives expansion from outside a click — spring-loading during a drag, for one.
   *
   * `notify` false leaves `expandedChange` unfired, for changes that shouldn't be persisted because
   * they're provisional and may be undone before the interaction finishes.
   */
  setExpanded(expanded: boolean, notify = true): void {
    if (this.expanded === expanded) {
      return;
    }
    this.expanded = expanded;
    // Not an input binding, so nothing else would mark this OnPush view.
    this.cdr.markForCheck();
    if (notify) {
      this.expandedChange.emit(expanded);
    }
  }

  onHeaderClick(): void {
    // Clicking the label mid-rename must not collapse the section out from under the editor.
    if (this.isRenaming$.getValue() || !this.expandable) {
      return;
    }
    this.toggle();
  }

  /**
   * Puts the label into edit mode — from the section's own menu, or from the parent right after
   * creating a folder. forceSelect() is a no-op while the textbox is disabled, so enabling has to
   * reach the DOM first.
   */
  startRename(): void {
    if (!this.renameable) {
      return;
    }
    this.dismissBottomSheet();
    this.isRenaming$.next(true);
    modifiedSetTimeout(() => this.labelTextbox?.forceSelect(), 0);
  }

  onRenameFinished(title: string): void {
    this.isRenaming$.next(false);
    // Empty means the edit was rejected as invalid, and the textbox has already reverted it.
    if (title) {
      this.renameCommitted.emit(title);
    }
  }

  /**
   * Bound on the button host, not its `clickEvent` output — stopping there would pre-empt
   * MatMenuTrigger's own listener on this element and the menu would never open.
   */
  onMenuButtonClick(event: Event): void {
    event.stopPropagation();

    if (this.screenSizeService.renderMenuAsBottomSheet) {
      this.openBottomSheet();
    }
  }

  dismissBottomSheet(): void {
    this.bottomSheetRef?.dismiss();
    this.bottomSheetRef = undefined;
    this.isMenuOpen$.next(false);
  }

  onMenuOpened(): void {
    this.isMenuOpen$.next(true);
  }

  onMenuClosed(): void {
    this.isMenuOpen$.next(false);
  }

  private openBottomSheet(): void {
    if (!this.bottomSheetContainer) {
      return;
    }

    this.isMenuOpen$.next(true);
    this.bottomSheetRef = this.bottomSheet.open(this.bottomSheetContainer, {
      panelClass: ['pencil-bottom-sheet', 'light-scheme'],
      closeOnNavigation: true,
    });

    this.bottomSheetRef
      .afterDismissed()
      .pipe(take(1))
      .subscribe(() => {
        this.bottomSheetRef = undefined;
        this.isMenuOpen$.next(false);
      });
  }
}
